import { selectFeatureFlags } from '@flopflip/react-redux';
import navigationConfig from 'app/configs/navigationConfig';
import { createSelector } from 'reselect';

import { checkIsPathAllowedWhileCertificationPending } from 'utils/certificationAccess';
import { checkIsHiddenByFF } from 'utils/featureFlags';
import { hasPermission } from 'utils/permissions';
import { getIsNativePlatform } from 'utils/platform';
import { naturalSortBy } from 'utils/sort';

import {
  getIsFromInternalCompany,
  getMasterPermissions,
  getMasterRole,
  selectIsBrokerOrSubCompanyUser,
  selectIsSubCompanyCertificationPending,
} from 'store/auth/selectors/master.selectors';
import { selectIsCrmEnabledForCompany } from 'store/companies/selectors/crmFeatures.selectors';

const selectNavigationAuthContext = createSelector(
  [getMasterRole, getMasterPermissions, getIsFromInternalCompany, selectIsBrokerOrSubCompanyUser],
  (masterRole, permissions, isFromInternalCompany, isBrokerOrSubCompanyUser) => ({
    masterRole,
    permissions,
    isFromInternalCompany,
    isBrokerOrSubCompanyUser,
  }),
);

export const selectNavigationConfig = createSelector(
  [
    selectFeatureFlags(),
    selectNavigationAuthContext,
    selectIsSubCompanyCertificationPending,
    selectIsCrmEnabledForCompany,
  ],
  (
    { memory: featureFlags },
    { masterRole, permissions, isFromInternalCompany, isBrokerOrSubCompanyUser },
    isCertificationPending,
    isCrmEnabledForCompany,
  ) => {
    const isNativePlatform = getIsNativePlatform();

    const getAllowedChildren = (children = []) =>
      children.reduce((acc, item) => {
        const {
          children,
          FF,
          isFFNegative,
          pageCode,
          skipPermissionCheck,
          isBrokerCompanyOnly,
          isCertificationPendingOnly,
          isCrmCompanyGated,
          isHiddenOnMobile,
          url,
        } = item;

        // While the sub-company's certification is pending the route guard bounces every surface
        // outside the certification flow, so an item pointing at one would render as a dead link.
        // Parents carry no url — they drop out anyway once all their children are filtered.
        const isReachableWhileCertificationPending =
          !isCertificationPending || !url || checkIsPathAllowedWhileCertificationPending(url);

        // The certification entry is the inverse case: it is the only navigable surface left while
        // the certification is pending (STP-339), and noise on every other company's header once
        // the certification is approved.
        const isShownForCertificationState = !isCertificationPendingOnly || isCertificationPending;

        const isAllowed =
          !(isHiddenOnMobile && isNativePlatform) &&
          isReachableWhileCertificationPending &&
          isShownForCertificationState &&
          (!isBrokerCompanyOnly || isBrokerOrSubCompanyUser) &&
          (!isCrmCompanyGated || isCrmEnabledForCompany) &&
          hasPermission({
            pageCode,
            permissions,
            masterRole,
            isFromInternalCompany,
            skipPermissionCheck,
          }) &&
          !checkIsHiddenByFF({ featureFlags, FF, isFFNegative });

        if (isAllowed) {
          const hasChildren = !!children;
          const allowedChildren = getAllowedChildren(children);

          if (!hasChildren) {
            acc.push(item);
          }

          if (hasChildren && allowedChildren.length) {
            // Sort each area's option list (and every nested sub-list, via the recursion)
            // alphabetically, case-insensitively, AFTER permission/feature-flag filtering.
            // Applied here at the single shared source so every consumer stays sorted —
            // the desktop header dropdowns and the mobile bottom-nav / More sheet alike.
            // The root array is returned unsorted, so the top-level area row keeps its
            // declared order.
            acc.push({ ...item, children: naturalSortBy(allowedChildren, 'title') });
          }
        }

        return acc;
      }, []);

    return getAllowedChildren(navigationConfig);
  },
);
